import React from 'react';
import { Activity, Wind, Droplets } from 'lucide-react';

export interface AQIDisplayProps {
  pm25?: number;
  co2?: number;
  tvoc?: number;
  showLabels?: boolean;
  compact?: boolean;
  className?: string;
}

/**
 * AQI Display Style01 - Card Layout
 * 
 * 顯示空氣品質指標的卡片式佈局
 * - PM2.5 細懸浮微粒
 * - CO2 二氧化碳
 * - TVOC 揮發性有機化合物 (可選)
 */
export const Style01_Card: React.FC<AQIDisplayProps> = ({
  pm25,
  co2,
  tvoc,
  showLabels = true,
  compact = false,
  className = ''
}) => {
  // AQI level helper for PM2.5
  const getAQILevel = (value: number): { level: string; color: string; bgColor: string } => {
    if (value <= 15) return { level: '優良', color: 'text-green-400', bgColor: 'bg-green-500/20' };
    if (value <= 35) return { level: '良好', color: 'text-lime-400', bgColor: 'bg-lime-500/20' };
    if (value <= 54) return { level: '普通', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' };
    if (value <= 150) return { level: '不健康', color: 'text-orange-400', bgColor: 'bg-orange-500/20' };
    return { level: '危險', color: 'text-red-400', bgColor: 'bg-red-500/20' };
  };

  // CO2 level helper
  const getCO2Level = (value: number): { level: string; color: string; bgColor: string } => {
    if (value <= 600) return { level: '優良', color: 'text-green-400', bgColor: 'bg-green-500/20' };
    if (value <= 1000) return { level: '良好', color: 'text-lime-400', bgColor: 'bg-lime-500/20' };
    if (value <= 1500) return { level: '普通', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' };
    if (value <= 2500) return { level: '偏高', color: 'text-orange-400', bgColor: 'bg-orange-500/20' };
    return { level: '過高', color: 'text-red-400', bgColor: 'bg-red-500/20' };
  };

  // TVOC level helper (µg/m³)
  const getTVOCLevel = (value: number): { level: string; color: string; bgColor: string } => {
    if (value <= 220) return { level: '優良', color: 'text-green-400', bgColor: 'bg-green-500/20' };
    if (value <= 660) return { level: '良好', color: 'text-lime-400', bgColor: 'bg-lime-500/20' };
    if (value <= 2200) return { level: '普通', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' };
    if (value <= 5500) return { level: '偏高', color: 'text-orange-400', bgColor: 'bg-orange-500/20' };
    return { level: '過高', color: 'text-red-400', bgColor: 'bg-red-500/20' };
  };

  const hasPM25 = pm25 !== undefined;
  const hasCO2 = co2 !== undefined;
  const hasTVOC = tvoc !== undefined;
  const itemCount = [hasPM25, hasCO2, hasTVOC].filter(Boolean).length;

  if (itemCount === 0) {
    return null;
  }

  const pm25Info = hasPM25 ? getAQILevel(pm25!) : null;
  const co2Info = hasCO2 ? getCO2Level(co2!) : null;
  const tvocInfo = hasTVOC ? getTVOCLevel(tvoc!) : null;

  const gridCols = itemCount === 1 ? 'grid-cols-1' : itemCount === 2 ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <div className={`grid ${gridCols} gap-3 ${className}`}>
      {/* PM2.5 Card */}
      {hasPM25 && pm25Info && (
        <div className={`${pm25Info.bgColor} rounded-xl ${compact ? 'p-2' : 'p-3'} border border-slate-700/50`}>
          <div className="flex items-center gap-2 mb-1">
            <Activity className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} ${pm25Info.color}`} />
            {showLabels && <span className="text-xs text-slate-400">PM2.5</span>}
          </div>
          <div className={`${compact ? 'text-xl' : 'text-2xl'} font-bold ${pm25Info.color}`}>
            {pm25}
            <span className="text-xs ml-1 font-normal text-slate-500">µg/m³</span>
          </div>
          {showLabels && <div className={`text-xs ${pm25Info.color} mt-1`}>{pm25Info.level}</div>}
        </div>
      )}
      
      {/* CO2 Card */}
      {hasCO2 && co2Info && (
        <div className={`${co2Info.bgColor} rounded-xl ${compact ? 'p-2' : 'p-3'} border border-slate-700/50`}>
          <div className="flex items-center gap-2 mb-1">
            <Wind className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} ${co2Info.color}`} />
            {showLabels && <span className="text-xs text-slate-400">CO₂</span>}
          </div>
          <div className={`${compact ? 'text-xl' : 'text-2xl'} font-bold ${co2Info.color}`}>
            {co2}
            <span className="text-xs ml-1 font-normal text-slate-500">ppm</span>
          </div>
          {showLabels && <div className={`text-xs ${co2Info.color} mt-1`}>{co2Info.level}</div>}
        </div>
      )}

      {/* TVOC Card */}
      {hasTVOC && tvocInfo && (
        <div className={`${tvocInfo.bgColor} rounded-xl ${compact ? 'p-2' : 'p-3'} border border-slate-700/50`}>
          <div className="flex items-center gap-2 mb-1">
            <Droplets className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} ${tvocInfo.color}`} />
            {showLabels && <span className="text-xs text-slate-400">TVOC</span>}
          </div>
          <div className={`${compact ? 'text-xl' : 'text-2xl'} font-bold ${tvocInfo.color}`}>
            {tvoc}
            <span className="text-xs ml-1 font-normal text-slate-500">µg/m³</span>
          </div>
          {showLabels && <div className={`text-xs ${tvocInfo.color} mt-1`}>{tvocInfo.level}</div>}
        </div>
      )}
    </div>
  );
};

export default Style01_Card;
